import React, { useState, useEffect } from 'react';
import { Box, Card, Container, Stack, TextField, Stepper, Step, StepLabel, Button, Grid, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import { clearStockBreakerData, getFromBreakerDetails, getStocks, updateStockBreaking } from '../../../redux/slices/stockManagementRedux/stockRedux';
import { useSelector } from '../../../redux/store';
import { safeJSONParse } from '../../../utils/common';
import { makeStyles } from '@mui/styles';
import { toast } from 'react-toastify';
import { notifyBossAboutStockTransferMistake } from '../../../redux/slices/stockManagementRedux/stockTransferRedux';

const steps = ['Enter From Barcode', 'Enter To Barcode'];
const useStyles = makeStyles({
  card: {
    margin: '24px',
    padding: '24px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
  },
  info: {
    fontSize: '16px',
    lineHeight: '24px',
  },
  label: {
    fontWeight: 'bold',
  },
});
const Breaker = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { themeStretch } = useSettings();
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [data, setData] = useState({ fromBarcode: '', fromBarcodeData: {}, toBarcode: '', toBarcodeQty: '' });
  const { stockBreakerData, processStockBreaking, error } = useSelector((state) => state?.stock);
  let selectedLocation;
  ({ selectedLocation } = useSelector((state) => state.location));

  let loggedInUser;
  ({ loggedInUser } = useSelector((state) => state.user));

  useEffect(() => {
    handleReset();
  }, []);

  const handleReset = () => {
    setIsUpdating(false);
    setActiveStep(0);
    setData({ fromBarcode: '', fromBarcodeData: {}, toBarcode: '', toBarcodeQty: '' });
  };

  const handleFromBarcodeEnter = (event, type) => {
    if (event.key === 'Enter') {
      event.target.select();
      const value = event.target.value;
      setData({ ...data, fromBarcode: value });
      dispatch(getFromBreakerDetails(value));
    }
  };
  const handleQty = (event, type) => {
    const value = event.target.value;
    setData({ ...data, [type]: value });
  };
  const handleToBarcodeEnter = (event, type) => {
    if (event.key === 'Enter') {
      if (isUpdating) {
        toast.error('Please wait until previous code breaks!');
      } else {
        setIsUpdating(true);
        event.target.select();
        const value = event.target.value;
        const payload = { ...data, [type]: value };
        setData(payload);
        dispatch(updateStockBreaking(payload));
      }
    }
  };

  useEffect(() => {
    if (data.fromBarcode?.length > 0) {
      dispatch(getStocks(-1, -1, -1, data.fromBarcode));
    }
  }, [data.fromBarcode, dispatch]);

  useEffect(() => {
    if (stockBreakerData?.length > 0) {
      const barcodeData = stockBreakerData?.[0];
      // STATUS CHECK
      let allowedStockStatuses = [1];
      if (!allowedStockStatuses.includes(barcodeData?.stockStatus)) {
        toast.error(`This stock cannot be broken. Current status: ${barcodeData?.stockStatusName || 'Unknown'}`);
        setData({ fromBarcode: '', fromBarcodeData: {} });
        setActiveStep(0);
        dispatch(clearStockBreakerData());
        return;
      }
      const stockLocation = safeJSONParse(barcodeData?.locationInfo);
      const sameLocation = stockLocation?.locationId === selectedLocation?.id;
      if (sameLocation) {
        setActiveStep(1);
        setData({
          ...data,
          fromBarcode: stockBreakerData?.[0]?.stockCode,
          fromBarcodeData: {
            ...barcodeData,
            foodInfo: safeJSONParse(barcodeData?.foodInfo),
            foodSupplierInfo: safeJSONParse(barcodeData?.foodSupplierInfo),
            locationInfo: safeJSONParse(barcodeData?.locationInfo),
            supplierInfo: safeJSONParse(barcodeData?.supplierInfo),
            unitInfo: safeJSONParse(barcodeData?.unitInfo),
          },
        });
        dispatch(clearStockBreakerData());
      } else {
        if (data?.fromBarcode?.length > 0) {
          setData({ ...data, fromBarcode: '', fromBarcodeData: {} });
          toast.error('Please select the same location barcode');
          dispatch(
            notifyBossAboutStockTransferMistake({
              code: barcodeData?.stockCode,
              locationId: selectedLocation?.id,
              tag: 'StockBreaking',
              message: `[Detected in Stock Breaker]\n\nBarcode: ${barcodeData?.stockCode}\nLocation: ${selectedLocation?.name}\nScanned By: ${loggedInUser?.name}`,
            })
          );
        }
      }
    }
  }, [stockBreakerData]);

  useEffect(() => {
    if (processStockBreaking?.length > 0) {
      setIsUpdating(false);
      const processStockData = processStockBreaking?.[0];
      setData({
        ...data,
        fromBarcodeData: {
          ...data?.fromBarcodeData,
          currentQty: processStockData?.currentQty,
        },
      });
    }
  }, [processStockBreaking]);

  useEffect(() => {
    if (error?.time) {
      setIsUpdating(false);
    }
  }, [error?.time]);

  return (
    <Page title='Stock Breaker'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Stock Breaker'
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Stock', href: PATH_DASHBOARD.stocks.transfer.sender }, { name: 'Availability Checker' }]}
          action={[]}
        />
        <Card sx={{ p: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box sx={{ p: 3 }}>
            {activeStep === 0 && (
              <TextField
                autoFocus
                margin='normal'
                label='From Barcode'
                fullWidth
                onClick={(event, type) => {
                  event.target.select();
                }}
                onKeyDown={(event) => handleFromBarcodeEnter(event, 'fromBarcode')}
              />
            )}
            {activeStep === 1 && (
              <Stack spacing={2}>
                <Card className={classes.card}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography className={classes.info}>
                        <span className={classes.label}>Code:</span> {data?.fromBarcodeData?.stockCode}
                      </Typography>
                      <Typography className={classes.info}>
                        <span className={classes.label}>Food Name:</span> {data?.fromBarcodeData?.foodInfo?.foodName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography className={classes.info}>
                        <span className={classes.label}>Current QTY:</span> {data?.fromBarcodeData?.currentQty}
                      </Typography>
                      <Typography className={classes.info}>
                        <span className={classes.label}>Initial QTY:</span> {data?.fromBarcodeData?.initialQty}
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>
                <TextField autoFocus margin='normal' label='QTY' fullWidth onChange={(event) => handleQty(event, 'toBarcodeQty')} />
                <TextField margin='normal' label='To Barcode' fullWidth onChange={(event) => handleQty(event, 'toBarcode')} onKeyDown={(event) => handleToBarcodeEnter(event, 'toBarcode')} />
              </Stack>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Button color='inherit' disabled={activeStep === 0} onClick={handleReset} sx={{ mr: 1 }}>
              Reset
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            {/*{activeStep !== 1 && <Button onClick={handleNext}>Next</Button>}*/}
          </Box>
        </Card>
      </Container>
    </Page>
  );
};

export default Breaker;
